// 問い合わせ機能の型定義
//
// commonDB.mise_common.inquiries テーブル（mise/loja/sazae 共通）の sazae 側 API レスポンス型。
// backend は snake_case で返すため、ここでも snake_case で揃える。

export type InquirySourceProduct = 'mise' | 'hub';

// MVP では status は open / closed の 2 値のみ。
// ボールがどちらにあるかは last_message_sender_type で導出するため、中間ステータスを持たない。
// V20260428_130000__shrink_inquiry_status_to_open_or_closed.sql の CHECK と整合させる。
export type InquiryStatus = 'open' | 'closed';

export type InquiryCategory = 'bug' | 'question' | 'feature_request' | 'data_issue' | 'other';

export type InquirySenderType = 'user' | 'admin';

export interface InquiryListItem {
  id: string;
  source_product: InquirySourceProduct;
  company_id: string;
  account_id: string;
  seller_id: string | null;
  seller_name: string | null;
  user_id: string;
  user_name: string | null;
  category: InquiryCategory;
  subject: string;
  status: InquiryStatus;
  last_message_at: string;
  last_message_sender_type: InquirySenderType;
  created_at: string;
}

export interface InquiryListResponse {
  inquiries: InquiryListItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface InquiryAttachmentDto {
  id: string;
  file_name: string;
  file_size: number;
  content_type: string;
  sort_order: number;
}

export interface InquiryMessageDto {
  id: string;
  sender_type: InquirySenderType;
  sender_user_id: string | null;
  sender_user_name: string | null;
  /** sazae 管理者の email。mise/hub 側には絶対表示しないが、sazae 内では担当者識別に使用する。 */
  sender_admin_id: string | null;
  body: string;
  created_at: string;
  attachments: InquiryAttachmentDto[];
}

export interface InquiryDetail {
  id: string;
  source_product: InquirySourceProduct;
  company_id: string;
  account_id: string;
  seller_id: string | null;
  seller_name: string | null;
  user_id: string;
  user_name: string | null;
  category: InquiryCategory;
  subject: string;
  status: InquiryStatus;
  last_message_at: string;
  last_message_sender_type: InquirySenderType;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
  messages: InquiryMessageDto[];
}

export interface InquiryUpdateResponse {
  success: boolean;
}

export interface InquiryAttachmentUrlResponse {
  url: string;
  expires_in: number;
}

// === API リクエスト ===

/**
 * 一覧 API で許可されるソート対象カラム。
 * バックエンド (ALLOWED_SORT_BY) と同期させる。
 */
export type InquirySortBy =
  | 'last_message_at'
  | 'created_at'
  | 'subject'
  | 'category'
  | 'status'
  | 'last_message_sender_type'
  | 'source_product'
  | 'company_id'
  | 'seller_id'
  | 'user_name';

export type InquirySortOrder = 'asc' | 'desc';

export interface ListInquiriesParams {
  source_product?: InquirySourceProduct;
  status?: InquiryStatus;
  category?: InquiryCategory;
  company_id?: string;
  account_id?: string;
  seller_id?: string;
  /** subject + 全メッセージ body の部分一致検索 */
  q?: string;
  offset?: number;
  limit?: number;
  sort_by?: InquirySortBy;
  sort_order?: InquirySortOrder;
}

export interface CreateAdminMessageRequest {
  body: string;
}

export interface UpdateInquiryStatusRequest {
  status: InquiryStatus;
}

export interface UpdateInquiryCategoryRequest {
  category: InquiryCategory;
}

// === UI 表示用ラベル ===

export const INQUIRY_STATUS_LABELS: Record<InquiryStatus, string> = {
  open: '対応中',
  closed: '終了',
};

export const INQUIRY_CATEGORY_LABELS: Record<InquiryCategory, string> = {
  bug: '不具合',
  question: '使い方の質問',
  feature_request: '機能要望',
  data_issue: 'データに関する問い合わせ',
  other: 'その他',
};

export const INQUIRY_SOURCE_PRODUCT_LABELS: Record<InquirySourceProduct, string> = {
  mise: 'mise',
  hub: 'hub',
};

/**
 * Material UI の Chip color 値。InquiriesPage / InquiryDetailPage 両方で参照する。
 */
export const INQUIRY_STATUS_CHIP_COLORS: Record<InquiryStatus, 'default' | 'warning'> = {
  open: 'warning',
  closed: 'default',
};

/**
 * 一覧と詳細で共通利用するタイムスタンプ表示。`YYYY/MM/DD HH:mm` 形式。
 */
export function formatInquiryDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
